import { useMemo } from 'react';
import { useState } from 'react';
import { ReactNode } from 'react';

export type PickerType = 'year' | 'quarter' | 'month' | 'day';

function getStringFromNode(val: ReactNode, key?: string): string {
  if (key === 'Период') {
    if (typeof val === 'string') {
      const [monthName, year] = val.split(' ');
      const months = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
      const monthNum = months.findIndex(m => m === monthName) + 1;
      if (monthNum && year) {
        return `${year}-${monthNum.toString().padStart(2, '0')}`;
      }
    }
  }
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  if (Array.isArray(val)) return val.map(v => getStringFromNode(v, key)).join(' ');
  if (
    val &&
    typeof val === 'object' &&
    'props' in val &&
    val.props &&
    typeof val.props === 'object' &&
    'children' in val.props
  ) {
    return getStringFromNode((val.props as any).children, key);
  }
  return '';
}

function getPeriodFilterString(date: Date | null, type: PickerType): string {
  if (!date) return '';
  const year = date.getFullYear();
  if (type === 'year') return String(year);
  if (type === 'month') return `${year}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
  if (type === 'quarter') {
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    return `${year}-Q${quarter}`;
  }
  if (type === 'day') return `${year}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  return '';
}

function isDateInPeriod(planPeriod: string, selectedDate: Date, pickerType: PickerType): boolean {
  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth() + 1;
  const day = selectedDate.getDate();

  if (pickerType === 'year') {
    if (/^\d{4}$/.test(planPeriod)) {
      return year === Number(planPeriod);
    }
    if (/^\d{4}-\d{2}$/.test(planPeriod)) {
      const [y] = planPeriod.split('-').map(Number);
      return year === y;
    }
    if (/^\d{4}-Q(\d)$/.test(planPeriod)) {
      const y = Number(planPeriod.slice(0, 4));
      return year === y;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(planPeriod)) {
      const [y] = planPeriod.split('-').map(Number);
      return year === y;
    }
  }
  if (pickerType === 'month') {
    if (/^\d{4}$/.test(planPeriod)) {
      return year === Number(planPeriod);
    }
    if (/^\d{4}-\d{2}$/.test(planPeriod)) {
      const [y, m] = planPeriod.split('-').map(Number);
      return year === y && month === m;
    }
    if (/^\d{4}-Q(\d)$/.test(planPeriod)) {
      const y = Number(planPeriod.slice(0, 4));
      const qNum = Number(planPeriod.match(/^(\d{4})-Q(\d)$/)?.[2]);
      const startMonth = (qNum - 1) * 3 + 1;
      const endMonth = startMonth + 2;
      return year === y && month >= startMonth && month <= endMonth;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(planPeriod)) {
      const [y, m] = planPeriod.split('-').map(Number);
      return year === y && month === m;
    }
  }
  if (pickerType === 'quarter') {
    const quarter = Math.floor((month - 1) / 3) + 1;
    if (/^\d{4}$/.test(planPeriod)) {
      return year === Number(planPeriod);
    }
    if (/^\d{4}-Q(\d)$/.test(planPeriod)) {
      const y = Number(planPeriod.slice(0, 4));
      const qNum = Number(planPeriod.match(/^(\d{4})-Q(\d)$/)?.[2]);
      return year === y && quarter === qNum;
    }
    if (/^\d{4}-\d{2}$/.test(planPeriod)) {
      const [y, m] = planPeriod.split('-').map(Number);
      const planQuarter = Math.floor((m - 1) / 3) + 1;
      return year === y && planQuarter === quarter;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(planPeriod)) {
      const [y, m] = planPeriod.split('-').map(Number);
      const planQuarter = Math.floor((m - 1) / 3) + 1;
      return year === y && planQuarter === quarter;
    }
  }
  if (pickerType === 'day') {
    if (/^\d{4}$/.test(planPeriod)) {
      return year === Number(planPeriod);
    }
    if (/^\d{4}-Q(\d)$/.test(planPeriod)) {
      const y = Number(planPeriod.slice(0, 4));
      const qNum = Number(planPeriod.match(/^(\d{4})-Q(\d)$/)?.[2]);
      const startMonth = (qNum - 1) * 3 + 1;
      const endMonth = startMonth + 2;
      return year === y && month >= startMonth && month <= endMonth;
    }
    if (/^\d{4}-\d{2}$/.test(planPeriod)) {
      const [y, m] = planPeriod.split('-').map(Number);
      return year === y && month === m;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(planPeriod)) {
      const [y, m, d] = planPeriod.split('-').map(Number);
      return year === y && month === m && day === d;
    }
  }
  return false;
}

export function useFilterPlans(filteredPlans: any[], initialFilters = {}, initialPeriodValue: Date | null = null, initialPeriodPickerType: PickerType = 'month') {
  const [filters, setFilters] = useState<{ [key: string]: string }>(initialFilters);
  const [periodValue, setPeriodValue] = useState<Date | null>(initialPeriodValue);
  const [periodPickerType, setPeriodPickerType] = useState<PickerType>(initialPeriodPickerType);

  const filterableKeys = useMemo(() => {
    if (!filteredPlans.length) return [];
    return Object.keys(filteredPlans[0]).filter(key => key !== 'Активность' && key !== 'План' && key !== 'Период');
  }, [filteredPlans]);

  const filterOptions = useMemo(() => {
    const options: { [key: string]: { value: string; label: string }[] } = {};
    filterableKeys.forEach(key => {
      let values = Array.from(new Set(filteredPlans.map(plan => getStringFromNode(plan[key], key))));
      options[key] = [{ value: '', label: 'Все' }, ...values.map(v => ({ value: v, label: v }))];
    });
    return options;
  }, [filteredPlans, filterableKeys]);

  const filteredByFilters = useMemo(() => {
    return filteredPlans.filter(plan => {
      if (periodValue) {
        const planPeriod = getStringFromNode(plan['Период'], 'Период');
        const result = isDateInPeriod(planPeriod, periodValue, periodPickerType);
        if (!result) return false;
      }
      return filterableKeys.every(key => {
        if (!filters[key]) return true;
        const strVal = getStringFromNode(plan[key], key);
        return strVal === filters[key];
      });
    });
  }, [filteredPlans, filters, filterableKeys, periodValue, periodPickerType]);

  return {
    filters,
    setFilters,
    periodValue,
    setPeriodValue,
    periodPickerType,
    setPeriodPickerType,
    filterableKeys,
    filterOptions,
    filteredByFilters,
  };
} 